import { LEError, LELoading, LESuccess, } from "@single-js/common/util/loading-error";
import { nullableToMaybe } from "@single-js/common/util/nullable";
import { multiSelect } from "@single-js/common/util/query";
import BigNumber from "@single-js/common/vendor/bignumber.js/bignumber";
import { buildTopologyAction, } from "data/actions/topology";
import { getEmptyNodeLiveMonitoringMetrics, } from "data/models";
import { differenceInSeconds } from "date-fns";
import _ from "lodash";
import { from, merge, of } from "rxjs";
import { logError } from "util/logging";
import * as logging from "util/logging";
import { makeActionCreator } from "worker/api/helpers";
import { SQL_AGGREGATORS_QUERY, SQL_LEAVES_QUERY, SQL_NODES_LIVE_CPU, SQL_NODES_LIVE_DISK, SQL_NODES_LIVE_MEMORY, } from "worker/api/topology-queries";
("@single-js/common/worker/api");
export function buildNodes([rawLeaves, rawAggregators]) {
    const leaves = _.map(rawLeaves, (rawLeaf) => ({
        host: rawLeaf.host,
        port: rawLeaf.port,
        state: rawLeaf.state,
        openedConnections: rawLeaf.openedConnections,
        averageLatency: rawLeaf.averageLatency || undefined,
        availabilityGroup: rawLeaf.availabilityGroup,
        role: "LEAF",
        pairHost: rawLeaf.pairHost,
        pairPort: nullableToMaybe(rawLeaf.pairPort),
        liveMonitoring: getEmptyNodeLiveMonitoringMetrics(),
    }));
    const aggregators = _.map(rawAggregators, (rawAggregator) => ({
        host: rawAggregator.host,
        port: rawAggregator.port,
        state: rawAggregator.state,
        openedConnections: rawAggregator.openedConnections,
        averageLatency: rawAggregator.averageLatency || undefined,
        role: rawAggregator.masterAggregator.eq(1)
            ? "MASTER_AGGREGATOR"
            : "AGGREGATOR",
        liveMonitoring: getEmptyNodeLiveMonitoringMetrics(),
    }));
    return _.concat(leaves, aggregators);
}
export const fetchTopologyData = (conn) => {
    return multiSelect(conn, { sql: SQL_LEAVES_QUERY }, { sql: SQL_AGGREGATORS_QUERY });
};
export const queryTopology = makeActionCreator({
    name: "queryTopology",
    handle: (ctx) => {
        const $loading = of({
            type: "TOPOLOGY",
            error: false,
            payload: { loading: true },
        });
        const startDate = new Date();
        const $compute = from(ctx.manager
            .getPooledConnection()
            .then((conn) => fetchTopologyData(conn)
            .then(buildNodes)
            .then((nodes) => buildTopologyAction({
            nodes,
            deltaTimeS: differenceInSeconds(new Date(), startDate),
        }))
            .catch((err) => {
            logError(err);
            return {
                type: "TOPOLOGY",
                error: true,
                payload: err.message,
            };
        })
            .finally(() => conn.release()))
            .catch((err) => {
            return {
                type: "TOPOLOGY",
                error: true,
                payload: err.message,
            };
        }));
        return merge($loading, $compute);
    },
});
// This function calculates the CPU usage for a given node given the results for
// the previous and the current mv_sysinfo_cpu query.
// ** This function has a few EARLY RETURNS and throws some errors to handle
// some special cases. **
function calculateNodeCpuUsagePercent(previous, current) {
    let nodeCpuUsage;
    let nodeCpuLimit;
    if (current.memsqlTotalUsedCumulativeNs === null) {
        throw new Error("current.totalUsedCumulativeNs should not be null");
    }
    if (current.timestampNs === null) {
        throw new Error("current.timestampNs should not be null");
    }
    if (previous) {
        if (previous.memsqlTotalUsedCumulativeNs === null) {
            throw new Error("previous.totalUsedCumulativeNs should not be null");
        }
        if (previous.timestampNs === null) {
            throw new Error("previous.timestampNs should not be null");
        }
        if (current.numCpus === null) {
            throw new Error("current.numCpus should not be null");
        }
        // nodeCpuUsage is defined in number of cpu seconds per second
        nodeCpuUsage = current.memsqlTotalUsedCumulativeNs
            .minus(previous.memsqlTotalUsedCumulativeNs)
            .dividedBy(current.timestampNs.minus(previous.timestampNs));
        nodeCpuLimit = current.numCpus;
        // This node's CPU usage is being managed via a cgroup
        if (current.cgroupQuotaNs) {
            if (current.cgroupPeriodNs === null) {
                throw new Error("sqlNodesLiveCPU.cgroupPeriodNs should not be null");
            }
            // If there is a previous value for cgroupQuotaNs and it is
            // different from the current one, we make `totalCpuUsagePercent`
            // undefined since a valid comparison cannot be made. An example
            // problematic case would be if the quota increases, then the delta
            // cpu time could be higher than the previous quota would have
            // allowed and we'd get a value over 100%.
            if (previous.cgroupQuotaNs !== null &&
                !current.cgroupQuotaNs.eq(previous.cgroupQuotaNs)) {
                return new LELoading();
            }
            else {
                // If the quota forms a tighter bound on CPU than the number of
                // physical CPUs, use that bound as the limit instead.
                nodeCpuLimit = BigNumber.min(nodeCpuLimit, current.cgroupQuotaNs.dividedBy(current.cgroupPeriodNs));
            }
        }
    }
    else {
        // If there are previous results, but not for this specific node, we
        // have an unknown CPU value.
        logging.log("error", `Found no previous CPU usage value for node ${current.nodeAddress}.`);
        return new LEError();
    }
    if (nodeCpuLimit === undefined) {
        return new LESuccess(nodeCpuUsage.toNumber());
    }
    else {
        return new LESuccess(nodeCpuUsage.dividedBy(nodeCpuLimit).toNumber());
    }
}
function buildNodesLiveMonitoringAction({ results: { sqlNodesLiveCPU, sqlNodesLiveMemory, sqlNodesLiveDisk }, previousResults, }) {
    const payload = {};
    // This function updates the payload for a given node address.
    function updatePayload(nodeAddress, newPartialPayload) {
        payload[nodeAddress] = {
            ...payload[nodeAddress],
            ...newPartialPayload,
        };
    }
    // Add CPU Usage to each node.
    _.forEach(sqlNodesLiveCPU, (current) => {
        let totalCpuUsagePercent = new LELoading();
        // If there are no previous results, we just let `totalCpuUsagePercent`
        // default to `undefined` (loading).
        if (previousResults) {
            const previous = _.find(previousResults.sqlNodesLiveCPU, (row) => row.nodeAddress === current.nodeAddress);
            totalCpuUsagePercent = calculateNodeCpuUsagePercent(previous, current);
        }
        updatePayload(current.nodeAddress, {
            totalCpuUsagePercent,
        });
    });
    // Add used and total system memory to each host.
    _.forEach(sqlNodesLiveMemory, (sqlNodesLiveMemoryRow) => {
        if (sqlNodesLiveMemoryRow.usedMemoryB === null) {
            throw new Error("sqlNodesLiveMemoryRow.memoryLimitB should not be null");
        }
        if (sqlNodesLiveMemoryRow.totalMemoryB === null) {
            throw new Error("sqlNodesLiveMemoryRow.totalMemoryB should not be null");
        }
        // If CGROUP_TOTAL_B in MV_SYSINFO_MEM is larger or equal than
        // HOST_TOTAL_B, this node's memory usage is not being limited by the
        // cgroup and so we just take the host's total memory and use that as
        // the total.
        let memoryLimitB = new LELoading();
        if (sqlNodesLiveMemoryRow.cgroupMemoryLimitB === null) {
            memoryLimitB = new LESuccess(sqlNodesLiveMemoryRow.totalMemoryB);
        }
        else {
            memoryLimitB = new LESuccess(BigNumber.min(sqlNodesLiveMemoryRow.cgroupMemoryLimitB, sqlNodesLiveMemoryRow.totalMemoryB));
        }
        updatePayload(sqlNodesLiveMemoryRow.nodeAddress, {
            memoryLimitB,
            usedMemoryB: new LESuccess(sqlNodesLiveMemoryRow.usedMemoryB),
        });
    });
    _.forEach(sqlNodesLiveDisk, (sqlNodesLiveDiskRow) => {
        if (sqlNodesLiveDiskRow.totalDiskB === null) {
            throw new Error("sqlNodesLiveDiskRow.totalDiskB should not be null");
        }
        if (sqlNodesLiveDiskRow.usedDiskB === null) {
            throw new Error("sqlNodesLiveDiskRow.usedDiskB should not be null");
        }
        updatePayload(sqlNodesLiveDiskRow.nodeAddress, {
            usedDiskB: new LESuccess(sqlNodesLiveDiskRow.usedDiskB),
            totalDiskB: new LESuccess(sqlNodesLiveDiskRow.totalDiskB),
        });
    });
    return {
        type: "NODES_LIVE_MONITORING",
        error: false,
        payload,
    };
}
const NODES_LIVE_MONITORING_SAGA_ID = "nodes-live-monitoring";
const NODES_LIVE_MONITORING_SAGA_INTERVAL_MS = 1000;
export const nodesLiveMonitoringStart = makeActionCreator({
    name: "nodesLiveMonitoringStart",
    handle(ctx, args) {
        const ipAddress = args && args.ipAddress;
        return ctx.sagas.register(NODES_LIVE_MONITORING_SAGA_ID, (observer) => {
            let running = true;
            let timeoutId;
            const emit = (action) => running && observer.next(action);
            let previousResults;
            const liveMonitor = () => {
                return ctx.manager
                    .getPooledConnection()
                    .then((conn) => {
                    multiSelect(conn, {
                        sql: SQL_NODES_LIVE_CPU(ipAddress),
                    }, {
                        sql: SQL_NODES_LIVE_MEMORY(ipAddress),
                    }, {
                        sql: SQL_NODES_LIVE_DISK(ipAddress),
                    })
                        .then((results) => {
                        const resultsObj = {
                            sqlNodesLiveCPU: results[0],
                            sqlNodesLiveMemory: results[1],
                            sqlNodesLiveDisk: results[2],
                        };
                        emit(buildNodesLiveMonitoringAction({
                            previousResults,
                            results: resultsObj,
                        }));
                        previousResults = resultsObj;
                    })
                        .catch((err) => {
                        logError(err);
                        emit({
                            type: "NODES_LIVE_MONITORING",
                            error: true,
                            payload: err.message,
                        });
                    })
                        .finally(() => {
                        conn.release();
                    });
                })
                    .catch((err) => {
                    emit({
                        type: "NODES_LIVE_MONITORING",
                        error: true,
                        payload: err.message,
                    });
                })
                    .finally(() => {
                    if (running) {
                        timeoutId = setTimeout(liveMonitor, NODES_LIVE_MONITORING_SAGA_INTERVAL_MS);
                    }
                });
            };
            liveMonitor();
            return () => {
                running = false;
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
            };
        });
    },
});
export const nodesLiveMonitoringStop = makeActionCreator({
    name: "nodesLiveMonitoringStop",
    handle(ctx) {
        ctx.sagas.stop(NODES_LIVE_MONITORING_SAGA_ID);
        return of({
            type: "NODES_LIVE_MONITORING_STOP",
            error: false,
            payload: {},
        });
    },
});
